import { useState, useEffect, useRef } from 'react';
import { getBaseURL } from '../services/apiClient';

interface UsernameValidationResult {
  isChecking: boolean;
  isAvailable: boolean | null;
  message: string;
  isRateLimited: boolean;
  isError: boolean;
}

interface CachedResult {
  isAvailable: boolean | null;
  message: string;
  isRateLimited: boolean;
  isError: boolean;
  timestamp: number;
}

// Session-based cache (cleared on page reload)
const usernameCache = new Map<string, CachedResult>();
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

export const useUsernameValidation = (username: string): UsernameValidationResult => {
  const [isChecking, setIsChecking] = useState(false);
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);
  const [message, setMessage] = useState('');
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [isError, setIsError] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    // Abort any pending request when username changes
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Reset state if username is empty or too short
    if (!username || username.trim().length < 3) {
      setIsAvailable(null);
      setMessage('');
      setIsChecking(false);
      setIsRateLimited(false);
      setIsError(false);
      return;
    }

    const trimmedUsername = username.trim().toLowerCase();

    // Check cache first
    const cached = usernameCache.get(trimmedUsername);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
      // Use cached result
      setIsAvailable(cached.isAvailable);
      setMessage(cached.message);
      setIsRateLimited(cached.isRateLimited);
      setIsError(cached.isError);
      setIsChecking(false);
      return;
    }

    // Debounce the API call
    const timeoutId = setTimeout(async () => {
      try {
        setIsChecking(true);

        // Create new abort controller for this request
        abortControllerRef.current = new AbortController();

        const response = await fetch(
          `${getBaseURL()}/api/auth/check-username/${encodeURIComponent(trimmedUsername)}`,
          { signal: abortControllerRef.current.signal }
        );
        const data = await response.json();

        let result: Omit<CachedResult, 'timestamp'>;

        if (response.ok) {
          result = {
            isAvailable: data.available,
            message: data.message,
            isRateLimited: false,
            isError: false
          };
        } else if (response.status === 429) {
          // Rate limit hit - don't mark username as unavailable
          // Don't cache rate limit errors
          result = {
            isAvailable: null,
            message: data.message || 'Too many username checks. Please try again later.',
            isRateLimited: true,
            isError: false
          };
        } else {
          // Other errors - don't cache errors
          result = {
            isAvailable: false,
            message: data.message || 'Error checking username',
            isRateLimited: false,
            isError: true
          };
        }

        // Cache successful results (not rate limits or errors)
        if (response.ok) {
          usernameCache.set(trimmedUsername, {
            ...result,
            timestamp: Date.now()
          });
        }

        // Update state
        setIsAvailable(result.isAvailable);
        setMessage(result.message);
        setIsRateLimited(result.isRateLimited);
        setIsError(result.isError);
      } catch (error) {
        // Ignore abort errors (user typed more characters)
        if (error instanceof Error && error.name === 'AbortError') {
          return;
        }

        console.error('Error checking username:', error);
        setIsAvailable(null);
        setMessage('Error checking username availability');
        setIsRateLimited(false);
        setIsError(true);
      } finally {
        setIsChecking(false);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [username]);

  return { isChecking, isAvailable, message, isRateLimited, isError };
};